<template>
  <div v-if="showTypeSelectionModal">
    <ProcessTypeSelectionModal
      :selected="processType"
      @confirmSelection="handleSetProcessType"
    />
  </div>
  <div v-else-if="isRecordRoute">
    <Form
      ref="form"
      class="RealisationDetail PageWrapper DetailGrid"
      @error="handleValidationError"
      @submit="handleSubmit"
    >
      <RealisationHeader
        :record="record"
        :feedback="feedback"
        :feedback-message="feedbackMessage"
        :step="currentStep"
        :record-step="step"
      >
        <FlowInteraction
          position="top"
          :first="currentStep === '1'"
          :step="step"
          :current-step="currentStep"
          :locked="locked"
          :disabled="disabled"
          :completed="completed"
          :is-completed-step="isCompletedStep"
          :disable-next-btn="isUnsavedRecord || disableNextBtn"
          :may-edit="mayEdit"
          :open-for-change="openForChange"
          @handleNext="handleNext"
          @handlePrevious="handlePrevious"
          @handleSubmitAndNext="handleSubmitAndNext"
          @handleCancelChange="handleCancelChange"
          @handleStartCorrection="handleStartCorrection"
          @handleStartChange="handleStartChange"
          @handleComplete="handleComplete"
        />
      </RealisationHeader>

      <RealisationSidebar
        :uuid="uuid"
        :disabled="disabled || locked"
      />

      <div class="DetailGrid__map">
        <ProcessTypeAlert
          :show="isUnsavedRecord"
          :process-type="processType"
          @click="handleChangeProcessType"
        />

        <b-alert
          :show="isGORALTenant && nextVersion"
          variant="warning"
        >
          De status van de oude en nieuwe locatie wordt pas bijgewerkt wanneer de locatie wijziging definitief wordt opgeslagen. Tot die tijd kan de wijziging zonder enige impact geannuleerd worden.
        </b-alert>
        <div class="DetailGrid__col1">
          <div v-if="isUnsavedRecord">
            <strong>Stakeholders</strong>
            <p>
              Om een proces te beginnen dienen eerst de relevante stakeholders te worden vastgelegd. Stakeholders waarvan gebruikers een actieve rol hebben in het proces zijn vervolgens niet meer aan te passen.
            </p>
            <p v-if="requestAdditions.length !== 0">
              Omdat u een nieuw proces start vanuit een aanvraag is de relevante gemeente reeds voorgeselecteerd.
            </p>
          </div>
          <StakeholderSelection
            :key="stakeHoldersKey"
            :values="StakeholderSelectionValues"
            :disabled="locked || disabled"
            :has-ref="! isUnsavedRecord"
            :lock="! isUnsavedRecord"
            inline-columns
            @setMunicipality="handleMunicipalityChange"
            @setStakeholders="handleStakeholdersChange"
            @input="handleInput"
          />
        </div>

        <TableManager
          v-if="! isUnsavedRecord && isGORALTenant"
          ref="TableManager"
          :loading="false"
          :source-records="[]"
          category="goral-locations-map"
          @refresh="handleFilterRefresh"
        >
          <MapPreSelectedLocationSelection
            :record="record"
            :values="values"
            :locked="locked"
            :disabled="disabled"
            :filters="activeFilters"
            :connect="! isUnsavedRecord && ! nextVersion"
            :municipality-code="municipalityCode"
            @setCoordinates="handleCoordinateChange"
          />
        </TableManager>
        <MapDisconnectedLocationSelection
          v-if="! isUnsavedRecord && !isGORALTenant"
          :record="record"
          :values="values"
          :locked="locked"
          :disabled="disabled"
          :municipality-code="municipalityCode"
          @setCoordinates="handleCoordinateChange"
        />
      </div>

      <div v-if="! isUnsavedRecord" class="DetailGrid__col1">
        <StepOneMiscBottom
          :values="values"
          :disabled="locked || disabled"
          :has-ref="!!record.ref"
        />
      </div>

      <div v-if="! isUnsavedRecord" class="DetailGrid__col2">
        <ChargingSituation
          :values="values"
          :disabled="locked || disabled"
        />
      </div>

      <div v-if="! isUnsavedRecord" class="DetailGrid__wide">
        <AutomatedViewsBtn
          :uuid="uuid"
          :disabled="locked || disabled"
        />
      </div>

      <div v-if="! isUnsavedRecord" class="DetailGrid__col3">
        <p>Boven aanzicht</p>
        <FilesManager
          :uuid="uuid"
          :disabled="disabled"
          :locked="locked"
          :wide="true"
          category="topview"
          view="thumbnails"
          linklabel="download"
        />
        <p
          v-if="locked && ! record.hasFileOfCategory({ category: 'topview' })"
          class="text-muted"
        >
          Er is geen boven aanzicht beschikbaar
        </p>
      </div>
      <div v-if="! isUnsavedRecord" class="DetailGrid__col4">
        <p>Zij aanzicht</p>

        <FilesManager
          :uuid="uuid"
          :disabled="disabled"
          :locked="locked"
          :wide="true"
          category="sideview"
          view="thumbnails"
          linklabel="download"
        />
        <p
          v-if="locked && ! record.hasFileOfCategory({ category: 'sideview' })"
          class="text-muted"
        >
          Er is geen zij aanzicht beschikbaar
        </p>
      </div>

      <div v-if="! isUnsavedRecord" class="DetailGrid__col5">
        <LocationPreparation
          :values="values"
          :disabled="locked || disabled"
        />
      </div>

      <div v-if="! isUnsavedRecord" class="DetailGrid__col6">
        <CPOFinUnit
          v-if="isCPO && isPNCTenant"
          :values="values"
          :disabled="locked || disabled"
        />

        <CPOAdditionalCosts
          :values="values"
          :disabled="locked || disabled"
        />

        <h3>Handmatig startdocument</h3>
        <FilesManager
          :uuid="uuid"
          :disabled="disabled"
          :locked="locked"
          :wide="true"
          category="parkingspots"
          view="list"
          linklabel="download"
        />
        <p
          v-if="(locked || disabled) && (values && values.Docs ? values.Docs.ParkingSpots : []).length === 0"
          class="text-muted"
        >
          Er is geen handmatig ingevuld startdocument beschikbaar.
        </p>
        <template v-if="(values && values.Docs ? values.Docs.ParkingSpots : []).length === 0">
          <DocDownload
            class="mb-3"
            :uuid="uuid"
          />
        </template>

      </div>

      <RealisationFooter
        :feedback="feedback"
        :feedback-message="feedbackMessage"
      >
        <FlowInteraction
          position="bottom"
          :first="currentStep === '1'"
          :step="step"
          :current-step="currentStep"
          :locked="locked"
          :disabled="disabled"
          :completed="completed"
          :is-completed-step="isCompletedStep"
          :disable-next-btn="isUnsavedRecord || disableNextBtn"
          :may-edit="mayEdit"
          :open-for-change="openForChange"
          @handleNext="handleNext"
          @handlePrevious="handlePrevious"
          @handleSubmitAndNext="handleSubmitAndNext"
          @handleCancelChange="handleCancelChange"
          @handleStartCorrection="handleStartCorrection"
          @handleStartChange="handleStartChange"
          @handleComplete="handleComplete"
        />
      </RealisationFooter>
    </Form>
    <Comments v-if="! record.isUnsavedRecord()" :uuid="uuid" :version="version" @sync="handleSync" />
  </div>
</template>

<script>

import RealisationHeader from '@/components/realisation/RealisationHeader.vue'
import RealisationFooter from '@/components/realisation/RealisationFooter.vue'
import RealisationSidebar from '@/components/realisation/RealisationSidebar.vue'

import Form from '@/components/form/Form.vue'
import MapDisconnectedLocationSelection from '@/components/map/MapDisconnectedLocationSelection.vue'
import MapPreSelectedLocationSelection from '@/components/map/MapPreSelectedLocationSelection.vue'
import ChargingSituation from '@/components/definitions/ChargingSituation.vue'
import StakeholderSelection from '@/components/definitions/StakeholderSelection.vue'
import StepOneMiscBottom from '@/components/definitions/StepOneMiscBottom.vue'

import CPOAdditionalCosts from '@/components/definitions/CPOAdditionalCosts.vue'
import CPOFinUnit from '@/components/definitions/CPOFinUnit.vue'
import LocationPreparation from '@/components/definitions/LocationPreparation.vue'
// import FileUpload from '@/components/FileUpload.vue'
import DocDownload from '@/components/realisation/DocDownload.vue'
import AutomatedViewsBtn from '@/components/realisation/AutomatedViewsBtn.vue'

import TableManager from '@/components/Table/TableManager.vue'

import ProcessTypeAlert from '@/components/realisation/ProcessTypeAlert.vue'
import ProcessTypeSelectionModal from '@/components/realisation/ProcessTypeSelectionModal.vue'

import Comments from '@/components/Comments.vue'

import FlowInteraction from '@/components/flow/FlowInteraction.vue'

import FilesManager from '@/components/files/FilesManager.vue'

import { uuidValidateV4 } from '@/services/validation'
import { getGridOperatorByMunicipalityCode } from '@/services/gridoperators'
import { contractorsByMunicipalityCode, operatorsByMunicipalityCode } from '@/config'

import BaseRealisationStep from '@/views/BaseRealisationStep.vue'

import { mapGetters, mapMutations } from 'vuex'

export default {
  name: 'RealisationDetail1',
  components: {
    RealisationSidebar, RealisationHeader, RealisationFooter,
    MapDisconnectedLocationSelection,
    MapPreSelectedLocationSelection,
    Form, ChargingSituation, StakeholderSelection, StepOneMiscBottom,
    CPOAdditionalCosts, LocationPreparation, CPOFinUnit,
    // FileUpload,
    Comments,
    DocDownload,
    AutomatedViewsBtn,
    FlowInteraction,
    ProcessTypeSelectionModal,
    ProcessTypeAlert,
    TableManager,
    FilesManager
  },
  mixins: [BaseRealisationStep],
  data() {
    return {
      nextVersion: false,
      processType: null,
      currentStep: '1',
      activeFilters: [],
      StakeholderSelectionValues: {},
      municipalityCode: '',
      stakeHoldersKey: 0
    }
  },
  computed: {
    ...mapGetters('tenant', [
      'isCurrentTenant',
    ]),
    ...mapGetters('stakeholders', [
      'getMunicipalities',
      'getContractors', 
      'getGridOperators',
      'getChargePointOperators',
      'getMunicipalitySlugByGeographyCode'
    ]),
    // For existing records this is handled by the parent component, but not when it concerns new Records
    requestAdditions() {
      if (this.$route.query.aanvragen) {
        return this.$route.query.aanvragen.split('|').filter(id => {
          return uuidValidateV4({ id }) && this.requestByUuid({ uuid: id })
        })
      }
      return []
    },

    // Docs
    minParkingSpotsDocsId() {
      try {
        return this.minIdByDocType({ doctype: 'ParkingSpots' })
        // return this.record.values.Docs.ParkingSpots.reduce((result, id) => Math.max(result,id), 0)
      } catch (e) {
        return 0
      }
    },
    minTopViewDocsId() {
      try {
        let Max = this.minIdByDocType({ doctype: 'TopView' })
        let MaxAlt = this.minIdByDocType({ doctype: 'TopView-alt' })
        return Math.max(Max, MaxAlt)
      } catch (e) {
        return 0
      }
    },
    minSideViewDocsId() {
      try {
        let Max = this.minIdByDocType({ doctype: 'SideView' })
        let MaxAlt = this.minIdByDocType({ doctype: 'SideView-alt' })
        return Math.max(Max, MaxAlt)
      } catch (e) {
        return 0
      }
    },
    /**
     * We currently also use the park-n-charge step components for go-ral and opcharge
     */
    isPNCTenant() {
      return this.isCurrentTenant({ tenant: 'park-n-charge' })
    },
    isGORALTenant() {
      return this.isCurrentTenant({ tenant: 'go-ral' })
    },
    isCPO() {
      return this.hasRole({ role: 'cpo' })
    },
    // minCPOAdditionalDocsId() {
    //   try {
    //     return this.record.values.Docs.CPOAdditional.reduce((result, id) => Math.max(result,id), 0)
    //   } catch(e) {
    //     return 0
    //   }
    // },

    showTypeSelectionModal() {
      return this.processType === null
        && ! this.record.hasProcessType()
        && this.record.isUnsavedRecord()
    },
    routeType() {
      return this.$route.params.type || null
    },
    gridOperator () {
      const code = this.municipalityCode
      return operatorsByMunicipalityCode[code] || getGridOperatorByMunicipalityCode({ code })
    }
  },
  watch: {
    requestAdditions() {
      this.addRequestsToRealisation()
    },

    /**
     * When the type changes, we need to clear the current form data.
     *  We do that by clearing the process type
     */
    routeType(newValue, oldValue) {
      if (newValue && oldValue) {
        this.processType = null
        this.$nextTick(() => {
          this.processType = newValue
        })
      } else if (newValue) {
        this.processType = newValue
      }
    },
    
    uuid: {
      immediate: true,
      handler() {
        if (this.isUnsavedRecord) {
          console.log("(RE)SET TO UNSAVED stakeholder info")
          this.StakeholderSelectionValues = this.getDefaultStakeholders()
        } else {
          this.StakeholderSelectionValues = {
            Municipality: this.record.getStakeholderSlugByCategory({
              category: 'municipality'
            }),
            Contractor: this.record.getStakeholderSlugByCategory({
              category: 'contractor'
            }),
            GridOperator: this.record.getStakeholderSlugByCategory({
              category: 'gridoperator'
            }),
            ChargePointOperator: this.record.getStakeholderSlugByCategory({
              category: 'cpo'
            })
          }
        }
      }
    }
  },
  created() {
    if (this.routeType) {
      this.processType = this.routeType
    } else if (this.hasRecord) {
      this.processType = this.record.processType || null
    }

    // Us next tick to process request relations with a short delay, 
    // to avoid having the "changeCount" cleared by the component 
    // boot process during the change processing
    this.$nextTick(
      this.addRequestsToRealisation
    )
    

    // Maybe redirect to active step
    if (this.hasRecord && this.step !== 1 && this.$route.name == 'Realisation') {
      this.$router.replace({
        name: `Realisation.Step${this.step}`,
        params: {
          uuid: this.uuid,
        },
      })
    }
  
  },
  methods: {
    ...mapMutations('realisation', [
      'replaceRecord',
    ]),
    prepData({ data }) {
      // Document numbers
      // data.Docs = {
      //   // (this.Docs.ParkingSpots || []).filter(file => file.progress === 100).map(file => file.id),
      //   ParkingSpots: this.prepDocData({ doctype: 'ParkingSpots' }),
      //   TopView: this.prepDocData({ doctype: 'TopView' }),
      //   SideView: this.prepDocData({ doctype: 'SideView' }),
      // }

      // Convert ParkingSpot Identifiers
      data.ParkingSpots.Identifiers = data.ParkingSpots?.Identifiers?.map(obj => obj.Identifier).filter(id => !!id) || []

      return {
        "ParkingSpots": {
          "Type": data.ParkingSpots.Type,
          "NumberOfChargingStations": data.ParkingSpots.NumberOfChargingStations,
          "NumberOfParkingSpots": data.ParkingSpots.NumberOfParkingSpots,
          "Identifiers": data.ParkingSpots.Identifiers
        },
        "LocationPreparation": {
          "RequiresLocationPreparation": data.LocationPreparation.RequiresLocationPreparation,
          "OtherReason": data.LocationPreparation.OtherReason
        },
        "Advice": {
          "AdditionalCostDescription": data.CPOAdvice.AdditionalCostDescription,
          "ChargingStationVersion": data.CPOAdvice.ChargingStationVersion || ''
        },
        "Remarks": data.Remarks
      }

      // data.nextVersion = this.nextVersion
      // return data
    },

    /**
     * Stub
     */
    isValidData({ data }) {

      if (this.isGORALTenant) {

        if (this.record.cbapi) {

          if (! this.record.hasConnectedLocation({ category: 'selected'})) {
            this.feedbackMessage = 'Selecteer eerst een locatie.'
            this.disabled = false
            this.feedback = 'danger'
            return false  
          }
        }

        else if (! data.Location.chargingpointUuid) {
          this.feedbackMessage = 'Selecteer eerst een locatie.'
          this.disabled = false
          this.feedback = 'danger'
          return false
        }
      }

      if (this.goingToNextStep) {
        // if ((this.Docs.ParkingSpots || []).filter(file => file.progress === 100).length == 0) {

        //   this.disabled = false
        //   this.feedback = 'danger'
        //   this.feedbackMessage = 'Een inrichtingsplan is vereist om verder te kunnen.'
        //   return false
        // }
        // if (data.ParkingSpots.Identifiers.length === 0) {
        //   this.disabled = false
        //   this.feedback = 'danger'
        //   this.feedbackMessage = 'Minimaal 1 parkeerplaats is vereist.'
        //   return false
        // }

        try {
          if (
            data.LocationPreparation.RequiresLocationPreparation.includes('Overige') &&
            data.LocationPreparation.OtherReason === ''
          ) {
            this.feedbackMessage = 'Vul een korte omschrijving van de overige voorbereidingen in.'
            throw new Error('')
          }

          // TODO: temp disabled. verifying with PNC if it should still be required after move to step 1
          // if ( data.Contractor === '') {
          //   this.feedbackMessage = 'Het proces kan niet verder zonder aannemer.'
          //   throw new Error("")
          // }
        } catch (e) {
          this.disabled = false
          this.feedback = 'danger'
          return false
        }
      }
      return true
    },

    /**
     * Handle Activation / de-activation of the form
     *  Used when the process is not currently in step 1.
     */
    handleCancelChange() {
      // Restore the last Location object to Current Location
      // TODO: Reset the LocationPicker Pin
      this.reloading = true

      this.$nextTick(() => {
        if (this.correction === false) {
          this.record.values.Location = this.record.history[0]
        }

        this.nextVersion = false
        this.reloading = false
        this.locked = true
        this.correction = false
      })
    },
    handleStartChange() {
      this.record.values.Location = this.record.values.Location.LocationCopy({
        version: this.record.nextLocationVersion(),
      })
      this.nextVersion = true
      this.locked = false
    },
    postSave() {
      this.nextVersion = false
    },
    handleStartCorrection() {
      this.locked = false
      this.correction = true
    },

    handleSetProcessType({ processType }) {
      this.record.setProcessType({ processType })
      this.processType = processType
    },
    handleChangeProcessType() {
      this.record.setProcessType({ processType: null })
      this.processType = null
    },

    /**
     * Pass on the active filters
     */
    handleFilterRefresh({ filters }) { // , sorting, settings
      this.activeFilters = filters
    },

    /**
     * Field interactions
     * TODO: rework this in v2
     * 
     * TODO: CBAPI - DEFERRED - change stakeholder relation (e.g. GridOperator by municipality) 
     */

    async handleStakeholdersChange(stakeholder) {

      // Don't try to update what has never been saved
      if (this.isUnsavedRecord) return 

      const response = await this.$cbapi.process.updateStakeholder({
        uuid: this.record.getUuid(),
        synced: this.record.getSyncedTS(), 
        slug: stakeholder.value,
      })

      if (response !== false){
        this.replaceRecord({
          record: response?.data,
        })
        this.$notify({
          success: true,
          title: 'Geslaagd',
          text: `De ${stakeholder.label} wijziging is opgeslagen.`
        })
      }
    },
     
    handleMunicipalityChange(code) {
      
      console.log("handleMunicipalityChange 2", code)
      
      this.record.setGeographyCode({
        code
      })

      const gridOperator = getGridOperatorByMunicipalityCode({ code })
      this.municipalityCode = code
   
      const contractorByMunicipality = contractorsByMunicipalityCode[code]

      this.record.values.Location.GridOperator = gridOperator?.slug

      this.StakeholderSelectionValues.GridOperator = gridOperator?.slug

      // Verify contractor
      const isMatchedContractorEnabled = this.getEnabledContractors
        .find(contractor => contractor.uuid === contractorByMunicipality)

      this.record.values.Location.Contractor = isMatchedContractorEnabled?.uuid || ''
    },
    handleInput({ name, value }) {
      this.record.values.Location[name] = value
    },
    handleCoordinateChange({ code }) {
      // Do not allow the municipality to be changed on stored records
      if (code && this.record.isUnsavedRecord()) {
        if (code !== this.municipalityCode) {
          this.stakeHoldersKey++
        }
        this.record.values.Location.Municipality = code
        this.handleMunicipalityChange(code)
      }
    },

    /**
     * Check for automatic default stakeholders
     *  (e.g. there is only 1 option)
     */
    getDefaultStakeholders() {
      let stakeholders = {
        Municipality: null,
        Contractor: null,
        GridOperator: null,
        ChargePointOperator: null
      }

      if (this.getMunicipalities.length === 1) {
        stakeholders.Municipality = this.getMunicipalities[0].slug
        this.handleMunicipalityChange(
          stakeholders.Municipality.split('-').reverse()[0]
        )

      /**
       * When creating a RP from a Request context, we can preset the municipality based on that context
       */
      } else if (this.requestAdditions.length !== 0) {

        // Get the first request
        const request = this.requestByUuid({ uuid: this.requestAdditions[0] })
        
        if (request) {
          const geographyCode = request.getGeographyCode()
          const municipalityStakeholderSlug = this.getMunicipalitySlugByGeographyCode({
            code: geographyCode
          })

          if (municipalityStakeholderSlug) {
            stakeholders.Municipality = municipalityStakeholderSlug
            this.handleMunicipalityChange(geographyCode)
          }
        }
      }

      if (this.getChargePointOperators.length === 1) {
        stakeholders.ChargePointOperator = this.getChargePointOperators[0].slug
      }
      if (this.getGridOperators.length === 1) {
        stakeholders.GridOperator = this.getGridOperators[0].slug
      }
      if (this.getContractors.length === 1) {
        stakeholders.Contractor = this.getContractors[0].slug
      }

      return stakeholders
    }
  },
}
</script>
